import { MindARThree } from "mind-ar/dist/mindar-image-three.prod.js";

/**
 * Sesión MindAR mínima para reconocer marker-4 (mapa de Chile).
 * Cuando el target es detectado, llama a onMapDetected() para que
 * el componente navegue a /mapa y libere la cámara.
 *
 * Requiere que /public/target-map.mind esté compilado con marker-4.png.
 * Usa la herramienta /compile-marker.html para generarlo.
 *
 * @param {{ container: HTMLElement, footer: HTMLElement, onMapDetected: () => void }} opts
 * @returns {{ start: () => Promise<void>, dispose: () => void }}
 */
export function createMapScanSession({ container, footer, onMapDetected }) {
  let disposed  = false;
  let triggered = false;

  const mindarThree = new MindARThree({
    container,
    imageTargetSrc: `${process.env.PUBLIC_URL || ""}/targets_volcan6.mind`,
    maxTrack:        1,
    filterMinCF:     0.001,
    filterBeta:      0.05,
    warmupTolerance: 5,
    missTolerance:   12,
  });

  const { renderer, scene, camera } = mindarThree;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  // targets_volcan6.mind tiene 6 imágenes (marker-1…6).
  // marker-4 está compilado en el índice 3 (base-0).
  const anchor = mindarThree.addAnchor(3);

  anchor.onTargetFound = () => {
    if (triggered || disposed) return;
    triggered = true;
    footer.innerText = "✓ Mapa de Chile detectado — abriendo…";
    // Pequeña pausa visual antes de navegar
    setTimeout(() => {
      if (!disposed) onMapDetected();
    }, 550);
  };

  anchor.onTargetLost = () => {
    if (!triggered) footer.innerText = "Apunta al marcador del mapa de Chile…";
  };

  const start = async () => {
    if (!window.isSecureContext) {
      footer.textContent =
        "⚠️ La cámara solo funciona en HTTPS. Abre la app con https://…";
      footer.classList.add("show");
      return;
    }

    footer.classList.add("show");
    footer.textContent = "Solicitando acceso a la cámara…";

    try {
      await mindarThree.start();
      footer.textContent = "Apunta al marcador del mapa de Chile…";
      renderer.setAnimationLoop(() => renderer.render(scene, camera));
    } catch (err) {
      const name = err?.name ?? "";
      let text = "No se pudo abrir la cámara. ";
      if (name === "NotAllowedError") {
        text += "Permite el acceso en el navegador e intenta de nuevo.";
      } else if (name === "NotReadableError") {
        text += "La cámara está en uso. Cierra otras apps y recarga.";
      } else if (String(err?.message).includes("404") || String(err?.message).includes("mind")) {
        text = "⚠️ Falta target-map.mind en /public/. Usa /compile-marker.html para generarlo.";
      } else {
        text += "Recarga e intenta de nuevo.";
      }
      footer.textContent = text;
      console.error("Error createMapScanSession:", err);
    }
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    try { renderer.setAnimationLoop(null); } catch (_) {}
    try { mindarThree.stop(); } catch (_) {}
    try {
      container.querySelectorAll("video").forEach((v) => {
        v.srcObject?.getTracks?.().forEach((t) => t.stop());
        v.srcObject = null;
      });
    } catch (_) {}
    try { renderer.dispose(); } catch (_) {}
  };

  return { start, dispose };
}
